import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, TrendingUp, TrendingDown, Star, Plus, AlertCircle,
  BarChart2, Activity, Users, Building, Calendar, ShoppingCart,
  ExternalLink, ChevronDown, ChevronUp, DollarSign, BookOpen, ChevronRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, Cell } from 'recharts';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import OrderModal from '../components/OrderModal';
import MobileSheet from '../components/MobileSheet';
import { useWatchlist } from '../hooks/useWatchlist';
import { DetailPageSkeleton } from '../components/SkeletonLoader';
import TradingChart from '../components/charts/TradingChart';
import EdgeScore from '../components/EdgeScore';
import { formatPrice, formatNumber } from '../utils/formatters';
import { useSocket } from '../context/SocketContext';
import { SCANNER_INFO, getDynamicDescription, getSignalStatus, formatSignalAge, STATUS_CONFIG, PATTERN_INFO } from './scanners/scannerConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Signal interpretation helpers ───────────────────────────────────────────

const TOOLTIPS = {
  vwap: {
    buy:   'Price above VWAP and holding — institutional support present',
    sell:  'Price below VWAP and failing to reclaim — sellers in control',
    avoid: 'Price chopping around VWAP — no clear directional bias',
  },
  ema: {
    buy:   'Price above EMA 9 & 20, EMA 9 above EMA 20 — uptrend confirmed',
    sell:  'Price below EMA 9 & 20, EMA 9 below EMA 20 — downtrend confirmed',
    avoid: 'EMA lines crossing frequently — no clear trend, stay out',
  },
  rsi: {
    buy:   'RSI crossing above 30 — coming out of oversold, look for entry',
    sell:  'RSI crossing below 70 — coming down from overbought, consider exit',
    avoid: 'RSI between 40–60 — no clear momentum, wait for conviction',
  },
  sma: {
    buy:   'Price above SMA 50 and SMA 20 above SMA 50 — strong uptrend',
    sell:  'Price below SMA 50 and SMA 20 below SMA 50 — strong downtrend',
    avoid: 'Price moving sideways around SMAs — no directional conviction',
  },
  atr: {
    buy:   'ATR rising with price breakout — move has strength behind it',
    sell:  'ATR rising with price breakdown — downside has momentum',
    avoid: 'Very low ATR — volatility compressed, weak moves likely',
  },
};

function TooltipContent({ tips, colors }) {
  return (
    <>
      <p className="text-[10px] font-semibold mb-1" style={{ color: '#16C784' }}>Buy signal</p>
      <p className="text-[11px] mb-3" style={{ color: colors.secondaryText }}>{tips.buy}</p>
      <p className="text-[10px] font-semibold mb-1" style={{ color: '#ef4444' }}>Sell signal</p>
      <p className="text-[11px] mb-3" style={{ color: colors.secondaryText }}>{tips.sell}</p>
      <p className="text-[10px] font-semibold mb-1" style={{ color: '#f59e0b' }}>Avoid</p>
      <p className="text-[11px]" style={{ color: colors.secondaryText }}>{tips.avoid}</p>
    </>
  );
}

function Tooltip({ tips }) {
  const [open, setOpen] = React.useState(false);
  const { colors } = useTheme();
  return (
    <span className="relative inline-block ml-1 align-middle">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs leading-none opacity-40 hover:opacity-80 transition-opacity"
        style={{ fontSize: '11px' }}
        aria-label="Show indicator guide"
      >ⓘ</button>

      {/* Desktop: absolute popover */}
      {open && (
        <div
          className="hidden sm:block absolute z-50 left-0 top-5 w-56 rounded-lg p-3 shadow-xl"
          style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.accentBorder}` }}
        >
          <TooltipContent tips={tips} colors={colors} />
          <button
            onClick={() => setOpen(false)}
            className="mt-3 text-[10px] opacity-50 hover:opacity-80"
            style={{ color: colors.secondaryText }}
          >close ×</button>
        </div>
      )}

      {/* Mobile: bottom sheet */}
      <MobileSheet open={open} onClose={() => setOpen(false)} title="Indicator Guide">
        <TooltipContent tips={tips} colors={colors} />
      </MobileSheet>
    </span>
  );
}

function SignalCard({ label, signal, signalColor, subtext, tooltipKey }) {
  const { colors } = useTheme();
  return (
    <div
      className="px-3 py-2.5 rounded-xl flex flex-col gap-1"
      style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}
    >
      <p className="text-[11px] font-medium flex items-center" style={{ color: colors.secondaryText }}>
        {label}
        {tooltipKey && <Tooltip tips={TOOLTIPS[tooltipKey]} />}
      </p>
      <p className="text-[15px] font-bold leading-tight" style={{ color: signalColor }}>
        {signal}
      </p>
      {subtext && (
        <p className="text-[10px]" style={{ color: colors.secondaryText }}>{subtext}</p>
      )}
    </div>
  );
}

// Interprets raw technical values into human-readable signals
function interpretSignals(technicals, stock) {
  const price   = stock?.current_price;
  const rsi     = technicals?.rsi_14;
  const ema9    = technicals?.ema_20;   // closest proxy — ema_20 from DB
  const ema20   = technicals?.ema_20;
  const sma20   = technicals?.sma_20;
  const sma50   = technicals?.sma_50;
  const atr     = technicals?.atr_14;
  // VWAP is not stored in market_features; use ema_20 as intraday proxy
  // (consistent with decision engine approach)
  const vwap    = technicals?.ema_20;

  // ── RSI ────────────────────────────────────────────────────────────────
  let rsiSignal = null, rsiColor = '#94a3b8';
  if (rsi != null) {
    if (rsi < 30)      { rsiSignal = 'Oversold';  rsiColor = '#16C784'; }
    else if (rsi < 40) { rsiSignal = 'Weak';       rsiColor = '#f97316'; }
    else if (rsi <= 60){ rsiSignal = 'Neutral';    rsiColor = '#94a3b8'; }
    else if (rsi <= 70){ rsiSignal = 'Strong';     rsiColor = '#6366F1'; }
    else               { rsiSignal = 'Overbought'; rsiColor = '#ef4444'; }
  }

  // ── VWAP Bias (intraday) ───────────────────────────────────────────────
  let vwapSignal = null, vwapColor = '#94a3b8';
  if (price != null && vwap != null) {
    const pct = ((price - vwap) / vwap) * 100;
    if (pct > 0.5)        { vwapSignal = 'Bullish';  vwapColor = '#16C784'; }
    else if (pct < -0.5)  { vwapSignal = 'Bearish';  vwapColor = '#ef4444'; }
    else                  { vwapSignal = 'Neutral';   vwapColor = '#94a3b8'; }
  }

  // ── EMA Trend (intraday) ──────────────────────────────────────────────
  // We have ema_20 only from DB; use sma_20 as second reference when ema_9 absent
  let emaSignal = null, emaColor = '#94a3b8';
  if (price != null && ema20 != null && sma20 != null) {
    if (price > ema20 && price > sma20)       { emaSignal = 'Uptrend';   emaColor = '#16C784'; }
    else if (price < ema20 && price < sma20)  { emaSignal = 'Downtrend'; emaColor = '#ef4444'; }
    else                                       { emaSignal = 'Choppy';    emaColor = '#f59e0b'; }
  }

  // ── SMA Trend (swing) ─────────────────────────────────────────────────
  let smaSignal = null, smaColor = '#94a3b8';
  if (price != null && sma20 != null && sma50 != null) {
    if (price > sma50 && sma20 > sma50)       { smaSignal = 'Uptrend';   smaColor = '#16C784'; }
    else if (price < sma50 && sma20 < sma50)  { smaSignal = 'Downtrend'; smaColor = '#ef4444'; }
    else if (price > sma50)                   { smaSignal = 'Above SMA 50'; smaColor = '#6366F1'; }
    else                                       { smaSignal = 'Below SMA 50'; smaColor = '#f97316'; }
  }

  // ── ATR Risk (swing) ─────────────────────────────────────────────────
  let atrSignal = null, atrColor = '#94a3b8';
  let atrSubtext = null;
  if (atr != null && price != null && price > 0) {
    const atrPct = (atr / price) * 100;
    if (atrPct > 2.5)      { atrSignal = 'High Risk';   atrColor = '#ef4444'; }
    else if (atrPct > 1.5) { atrSignal = 'Medium Risk'; atrColor = '#f59e0b'; }
    else                   { atrSignal = 'Low Risk';    atrColor = '#16C784'; }
    atrSubtext = `ATR: ${atr.toFixed(2)} (${atrPct.toFixed(1)}% of price)`;
  }

  return {
    rsi:     { signal: rsiSignal,  color: rsiColor,  subtext: rsi  != null ? `RSI: ${rsi.toFixed(1)}` : null },
    vwap:    { signal: vwapSignal, color: vwapColor, subtext: vwap != null ? `EMA 20 proxy: ${vwap.toFixed(2)}` : null },
    ema:     { signal: emaSignal,  color: emaColor,  subtext: ema20 != null ? `EMA 20: ${ema20.toFixed(2)}` : null },
    sma:     { signal: smaSignal,  color: smaColor,  subtext: sma50 != null ? `SMA 50: ${sma50.toFixed(2)}` : null },
    atr:     { signal: atrSignal,  color: atrColor,  subtext: atrSubtext },
  };
}

function TechnicalSignals({ technicals, stock, colors, scannerSignals = [], chartPatterns = [] }) {
  const [tab, setTab] = React.useState('intraday');

  if (!technicals) return null;

  const sig = interpretSignals(technicals, stock);

  const intradayCards = [
    { label: 'Bias',     tooltipKey: 'vwap', ...sig.vwap },
    { label: 'Trend',    tooltipKey: 'ema',  ...sig.ema  },
    { label: 'Momentum', tooltipKey: 'rsi',  ...sig.rsi  },
  ];

  const swingCards = [
    { label: 'Trend',    tooltipKey: 'sma', ...sig.sma },
    { label: 'Momentum', tooltipKey: 'rsi', ...sig.rsi },
    { label: 'Risk',     tooltipKey: 'atr', ...sig.atr },
  ];

  const cards = tab === 'intraday' ? intradayCards : swingCards;

  const formatPatternAge = (detectedAt) => {
    if (!detectedAt) return null;
    const diff = Date.now() - new Date(detectedAt).getTime();
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor(diff / 3600000);
    if (days >= 1) return `${days}d ago`;
    if (hours >= 1) return `${hours}h ago`;
    return `${Math.floor(diff / 60000)}m ago`;
  };

  return (
    <div
      className="app-card rounded-xl px-3 py-2.5"
      style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}
    >
      {/* Header + tabs */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-4 h-4" style={{ color: colors.accent }} />
          <h2 className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>
            Technical Signals
          </h2>
        </div>
        <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
          {['intraday', 'swing'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="px-3 py-1 text-[11px] font-medium transition-colors capitalize whitespace-nowrap flex-shrink-0"
              style={{
                backgroundColor: tab === t ? colors.accent : 'transparent',
                color:           tab === t ? '#fff'        : colors.secondaryText,
              }}
            >
              {t === 'intraday' ? 'Intraday' : 'Swing'}
            </button>
          ))}
        </div>
      </div>

      {/* ── Technical signal cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {cards.map((card, idx) => (
          card.signal
            ? <SignalCard key={idx} {...card} />
            : (
              <div
                key={idx}
                className="px-3 py-2.5 rounded-xl flex flex-col gap-1"
                style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}
              >
                <p className="text-[11px] font-medium" style={{ color: colors.secondaryText }}>{card.label}</p>
                <p className="text-[11px]" style={{ color: colors.secondaryText, opacity: 0.5 }}>Data unavailable</p>
              </div>
            )
        ))}
      </div>
      <p className="text-[10px] mt-2 text-right" style={{ color: colors.secondaryText, opacity: 0.4 }}>
        {tab === 'intraday'
          ? 'VWAP proxy uses EMA 20 · signals update after market close'
          : 'Based on daily EOD data · updated after market close'}
      </p>

      {/* ── Chart Patterns ── */}
      {chartPatterns.length > 0 && (
        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${colors.border}40` }}>
          <p className="text-[11px] font-semibold mb-2" style={{ color: colors.secondaryText }}>Chart Patterns</p>
          <div className="grid grid-cols-2 gap-2">
            {chartPatterns.map((pattern, idx) => {
              const info = PATTERN_INFO[pattern.pattern_type] || { label: pattern.pattern_type.replace(/_/g, ' '), color: colors.accent };
              const statusCfg = STATUS_CONFIG[pattern.pattern_status] || STATUS_CONFIG.DETECTED;
              return (
                <div key={idx} className="p-2.5 rounded-lg"
                  style={{ backgroundColor: colors.inputBg, borderWidth: '1px', borderColor: colors.border }}>
                  <div className="flex items-center justify-between mb-1 gap-1">
                    <span className="text-[12px] font-semibold truncate" style={{ color: info.color }}>{info.label}</span>
                    <span className="text-[10px] px-1 py-0.5 rounded flex-shrink-0 font-bold"
                      style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}>
                      {statusCfg.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px]" style={{ color: colors.secondaryText }}>{info.description || ''}</span>
                    {pattern.detected_at && (
                      <span className="text-[10px] flex-shrink-0 ml-1" style={{ color: colors.secondaryText, opacity: 0.6 }}>
                        {formatPatternAge(pattern.detected_at)}
                      </span>
                    )}
                  </div>
                  {pattern.confidence && (
                    <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${pattern.confidence === 'high' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                      {pattern.confidence}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Scanner Signals ── */}
      {scannerSignals.length > 0 && (
        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${colors.border}40` }}>
          <p className="text-[11px] font-semibold mb-2" style={{ color: colors.secondaryText }}>Scanner Signals</p>
          <div className="grid grid-cols-2 gap-2">
            {scannerSignals.map((signal, idx) => {
              const info = SCANNER_INFO[signal.scanner_name] || { label: signal.scanner_name.replace(/_/g, ' '), color: colors.accent, direction: 'neutral', description: '' };
              const sigStatus = getSignalStatus(signal.triggered_at);
              const isBullish = info.direction === 'bullish';
              const isBearish = info.direction === 'bearish';
              const dirColor  = isBullish ? colors.profit : isBearish ? colors.loss : colors.accent;
              const isStrong  = (signal.strengthPct ?? 0) >= 75;
              const desc = getDynamicDescription(signal, info, signal.current_price);
              return (
                <div key={idx} className="p-2.5 rounded-lg"
                  style={{
                    backgroundColor: colors.inputBg,
                    borderWidth: isStrong ? '1.5px' : '1px',
                    borderColor: isStrong ? dirColor : colors.border,
                    boxShadow: isStrong ? `0 0 10px ${dirColor}20` : 'none',
                  }}
                >
                  <div className="flex items-center justify-between mb-1 gap-1">
                    <span className="text-[12px] font-semibold truncate" style={{ color: dirColor }}>{info.label}</span>
                    <span className="text-[10px] px-1 py-0.5 rounded flex-shrink-0"
                      style={{ backgroundColor: sigStatus === 'fresh' ? 'rgba(34,197,94,0.15)' : sigStatus === 'active' ? 'rgba(59,130,246,0.15)' : 'rgba(107,114,128,0.15)',
                               color: sigStatus === 'fresh' ? '#16C784' : sigStatus === 'active' ? '#6366F1' : '#6b7280' }}>
                      ● {formatSignalAge(signal.triggered_at) || sigStatus}
                    </span>
                  </div>
                  <span className="inline-block px-1.5 py-0.5 rounded text-[10px] capitalize mb-1.5"
                    style={{ backgroundColor: `${dirColor}20`, color: dirColor }}>
                    {info.direction}
                  </span>
                  <p className="text-[11px] leading-snug mb-2 line-clamp-2" style={{ color: colors.secondaryText }}>{desc}</p>
                  <div className="flex items-center gap-1.5">
                    <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: colors.border }}>
                      <div className="h-full rounded-full" style={{ width: `${signal.strengthPct ?? 50}%`, backgroundColor: dirColor }} />
                    </div>
                    <span className="text-[11px] font-semibold" style={{ color: dirColor }}>{signal.strengthPct ?? 50}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section-level tooltip ────────────────────────────────────────────────────

function SectionTooltip({ tip }) {
  const [open, setOpen] = React.useState(false);
  const { colors } = useTheme();
  return (
    <span className="relative inline-block align-middle ml-1">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs leading-none opacity-40 hover:opacity-80 transition-opacity"
        style={{ fontSize: '11px' }}
        aria-label="About this section"
      >ⓘ</button>

      {/* Desktop: absolute popover */}
      {open && (
        <div
          className="hidden sm:block absolute z-50 left-0 top-5 w-60 rounded-lg p-3 shadow-xl"
          style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.accentBorder}` }}
        >
          <p className="text-[11px] leading-relaxed" style={{ color: colors.secondaryText }}>{tip}</p>
          <button
            onClick={() => setOpen(false)}
            className="mt-2 text-[10px] opacity-50 hover:opacity-80"
            style={{ color: colors.secondaryText }}
          >close ×</button>
        </div>
      )}

      {/* Mobile: bottom sheet */}
      <MobileSheet open={open} onClose={() => setOpen(false)} title="About this section">
        <p className="text-[13px] leading-relaxed" style={{ color: colors.secondaryText }}>{tip}</p>
      </MobileSheet>
    </span>
  );
}

// ─── Shareholding Pattern — Context Layer ─────────────────────────────────────

function ShareholdingInsight({ stock, colors }) {
  const { promoter_holding, fii_holding, dii_holding, public_holding } = stock;
  const available = [promoter_holding, fii_holding, dii_holding, public_holding].filter(v => v != null);
  if (available.length < 2) return null;

  const segments = [
    { label: 'Promoter',  value: promoter_holding, color: '#6366f1' },
    { label: 'FII',       value: fii_holding,      color: '#16C784' },
    { label: 'DII',       value: dii_holding,      color: '#38bdf8' },
    { label: 'Public',    value: public_holding,   color: '#f59e0b' },
  ].filter(s => s.value != null);

  const interpPromoter = (v) => v >= 65 ? 'Strong control' : v >= 50 ? 'Good control' : v >= 40 ? 'Moderate' : 'Weak control';
  const interpFII      = (v) => v >= 20 ? 'High interest'  : v >= 10 ? 'Moderate'     : 'Low interest';
  const interpDII      = (v) => v >= 15 ? 'High'           : v >= 8  ? 'Moderate'     : 'Low';
  const interpPublic   = (v) => v >= 30 ? 'High vol. risk' : v >= 15 ? 'Moderate'     : 'Low volatility';
  const interps = { Promoter: interpPromoter, FII: interpFII, DII: interpDII, Public: interpPublic };

  const getSummary = () => {
    const inst = (fii_holding ?? 0) + (dii_holding ?? 0);
    const p = promoter_holding; const pub = public_holding ?? 0;
    if (p != null && p >= 65 && inst >= 20) return 'Strong promoter control with solid institutional backing';
    if (p != null && p >= 65)               return 'Strong promoter control, low institutional participation';
    if (p != null && p >= 50 && inst >= 25) return 'Balanced ownership with healthy institutional interest';
    if (p != null && p < 40 && pub > 30)    return 'Weak promoter control and high public holding — higher volatility risk';
    if (inst >= 30)                          return 'Heavy institutional participation signals market confidence';
    return 'Moderate ownership structure';
  };

  // Custom donut rendered as SVG (avoids dependency on Recharts PieChart quirks with donut label)
  const total = segments.reduce((s, x) => s + (x.value ?? 0), 0);
  const R = 52, cx = 68, cy = 68, stroke = 18;
  let cumAngle = -Math.PI / 2;
  const arcs = segments.map(seg => {
    const frac = (seg.value ?? 0) / total;
    const angle = frac * 2 * Math.PI;
    const x1 = cx + R * Math.cos(cumAngle);
    const y1 = cy + R * Math.sin(cumAngle);
    cumAngle += angle;
    const x2 = cx + R * Math.cos(cumAngle);
    const y2 = cy + R * Math.sin(cumAngle);
    const large = angle > Math.PI ? 1 : 0;
    return { ...seg, d: `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`, frac };
  });

  return (
    <div className="app-card rounded-xl px-3 py-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" style={{ color: colors.accent }} />
          <h2 className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Shareholding Pattern</h2>
          <SectionTooltip tip="Shows who owns the company. Higher promoter and institutional holding usually indicates stability, while high public holding can increase volatility." />
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>Context</span>
      </div>

      <div className="flex items-center gap-4">
        {/* Donut SVG */}
        <svg width="100" height="100" viewBox="0 0 136 136" className="sm:w-[136px] sm:h-[136px]" style={{ flexShrink: 0 }}>
          {arcs.map((arc, i) => (
            <path
              key={i}
              d={arc.d}
              fill="none"
              stroke={arc.color}
              strokeWidth={stroke}
              strokeLinecap="butt"
            />
          ))}
          {/* Center label */}
          <text x={cx} y={cy - 6} textAnchor="middle" style={{ fontSize: 11, fill: colors.secondaryText }}>Holding</text>
          <text x={cx} y={cy + 10} textAnchor="middle" style={{ fontSize: 13, fontWeight: 700, fill: colors.primaryText }}>
            {promoter_holding != null ? `${promoter_holding.toFixed(0)}%` : '—'}
          </text>
          <text x={cx} y={cy + 24} textAnchor="middle" style={{ fontSize: 9, fill: colors.secondaryText }}>Promoter</text>
        </svg>

        {/* Legend */}
        <div className="flex flex-col gap-2 flex-1">
          {segments.map((seg, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: seg.color }} />
                <span className="text-[12px]" style={{ color: colors.secondaryText }}>{seg.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${seg.color}18`, color: seg.color }}>
                  {interps[seg.label](seg.value)}
                </span>
                <span className="text-[13px] font-bold tabular-nums w-10 text-right" style={{ color: colors.primaryText }}>
                  {seg.value.toFixed(1)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[10px] mt-3 leading-relaxed italic" style={{ color: colors.secondaryText, opacity: 0.7 }}>
        {getSummary()}
      </p>
    </div>
  );
}

// ─── 52W Range Bar ────────────────────────────────────────────────────────────

function RangeBar52W({ low, high, current, colors }) {
  if (low == null || high == null || current == null || high === low) return null;
  const pct = Math.min(100, Math.max(0, ((current - low) / (high - low)) * 100));
  const fmt = (v) => v >= 1000 ? `₹${(v/1000).toFixed(0)}K` : `₹${Math.round(v)}`;
  return (
    <div className="flex flex-col gap-1 min-w-0 flex-1">
      <span className="text-[10px] uppercase tracking-wide font-medium" style={{ color: colors.secondaryText, opacity: 0.6 }}>52W Range</span>
      <div className="relative h-1.5 rounded-full overflow-visible"
        style={{ background: 'linear-gradient(to right, #ef4444 0%, #f59e0b 40%, #16C784 100%)' }}
      >
        <div
          className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-white shadow"
          style={{ left: `calc(${pct}% - 4px)`, backgroundColor: '#fff', transition: 'left 0.4s ease' }}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[9px] tabular-nums" style={{ color: '#ef4444' }}>{fmt(low)}</span>
        <span className="text-[9px] tabular-nums" style={{ color: '#16C784' }}>{fmt(high)}</span>
      </div>
    </div>
  );
}

// ─── Quarterly Financials ─────────────────────────────────────────────────────

function fmtCr(v) {
  if (v == null) return '—';
  if (Math.abs(v) >= 100000) return `${(v / 100000).toFixed(1)}L Cr`;
  if (Math.abs(v) >= 1000)   return `${(v / 1000).toFixed(0)}K Cr`;
  return `${Math.round(v)} Cr`;
}

function PctChange({ value }) {
  const { colors } = useTheme();
  if (value == null) return <span style={{ color: colors.secondaryText }}>—</span>;
  const color = value >= 0 ? '#16C784' : '#ef4444';
  return <span style={{ color }} className="font-semibold">{value >= 0 ? '+' : ''}{value}%</span>;
}

const CustomBarTooltip = ({ active, payload, label, colors }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-[11px] shadow-xl" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
      <p className="font-semibold mb-1" style={{ color: colors.primaryText }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {fmtCr(p.value)}</p>
      ))}
    </div>
  );
};

// ─── Financial Statements ─────────────────────────────────────────────────────

// Raw rupees → Crores, then Indian comma format (e.g. 2,64,905)
function toCr(v) {
  if (v == null || v === 0) return null;
  return Math.round(v / 1e7);
}
function fmtIndian(n) {
  if (n == null) return '—';
  const neg = n < 0;
  const abs = Math.abs(n);
  const s = abs.toLocaleString('en-IN');
  return neg ? `-${s}` : s;
}
function pctOf(num, den) {
  if (!num || !den || den === 0) return null;
  return Math.round((num / den) * 100);
}

// Row definition types:
// { label, key, bold, sep }          — direct key from data JSONB (raw rupees)
// { label, compute, bold, isPct }    — computed from period data
// { label, isEps, key }              — EPS: already in ₹/share
// { sep: true }                      — visual divider row

const PL_ROWS = [
  { label: 'Sales',              key: 'Total Revenue' },
  { label: 'Expenses',           key: 'Total Expenses' },
  { label: 'Operating Profit',   key: 'Operating Income',   bold: true },
  { label: 'OPM %',              compute: p => pctOf(p['Operating Income'], p['Total Revenue']), isPct: true },
  { label: 'Other Income',       key: 'Other Non Operating Income Expenses' },
  { label: 'Interest',           key: 'Interest Expense Non Operating' },
  { label: 'Depreciation',       key: 'Reconciled Depreciation' },
  { label: 'Profit before tax',  key: 'Pretax Income',      bold: true },
  { label: 'Tax %',              compute: p => pctOf(p['Tax Provision'], p['Pretax Income']), isPct: true },
  { label: 'Net Profit',         key: 'Net Income',         bold: true },
  { label: 'EPS in Rs',          key: 'Basic EPS',          isEps: true },
];

const BS_ROWS = [
  { label: 'Equity Capital',    key: 'Capital Stock' },
  { label: 'Reserves',          compute: p => toCr((p['Retained Earnings']||0) + (p['Additional Paid In Capital']||0) + (p['Common Stock Equity']||0) - (p['Capital Stock']||0)), isAlready: true },
  { label: 'Borrowings',        compute: p => toCr((p['Long Term Debt And Capital Lease Obligation']||p['Long Term Debt']||0) + (p['Current Debt And Capital Lease Obligation']||p['Current Debt']||0)), isAlready: true },
  { label: 'Other Liabilities', compute: p => toCr((p['Total Non Current Liabilities Net Minority Interest']||0) + (p['Current Liabilities']||0) - (p['Long Term Debt And Capital Lease Obligation']||p['Long Term Debt']||0) - (p['Current Debt And Capital Lease Obligation']||p['Current Debt']||0)), isAlready: true },
  { label: 'Total Liabilities', key: 'Total Liabilities Net Minority Interest', bold: true },
  { label: 'Fixed Assets',      key: 'Net PPE' },
  { label: 'CWIP',              key: 'Construction In Progress' },
  { label: 'Investments',       compute: p => toCr((p['Investmentin Financial Assets']||0) + (p['Long Term Equity Investment']||0) + (p['Available For Sale Securities']||0)), isAlready: true },
  { label: 'Other Assets',      key: 'Other Non Current Assets' },
  { label: 'Total Assets',      key: 'Total Assets', bold: true },
];

const CF_ROWS = [
  { label: 'Cash from Operating', key: 'Operating Cash Flow' },
  { label: 'Cash from Investing', key: 'Investing Cash Flow' },
  { label: 'Cash from Financing', key: 'Financing Cash Flow' },
  { label: 'Net Cash Flow',       key: 'Changes In Cash',     bold: true },
  { label: 'Free Cash Flow',      key: 'Free Cash Flow' },
];

function getRowValue(row, period) {
  if (row.isPct) {
    const v = row.compute(period);
    return v != null ? `${v}%` : '—';
  }
  if (row.isEps) {
    const v = period[row.key];
    return v != null ? `₹${Number(v).toFixed(2)}` : '—';
  }
  if (row.isAlready) {
    const v = row.compute(period);
    return (v != null && v !== 0) ? fmtIndian(v) : '—';
  }
  if (row.compute) {
    const v = row.compute(period);
    return v != null ? fmtIndian(v) : '—';
  }
  const raw = period[row.key];
  const cr = toCr(raw);
  return cr != null ? fmtIndian(cr) : '—';
}

function getRowNumeric(row, period) {
  if (row.isPct || row.isEps) return null;
  if (row.isAlready) { const v = row.compute(period); return (v != null && v !== 0) ? v : null; }
  if (row.compute) return row.compute(period);
  return toCr(period[row.key]);
}

function ScreenerTable({ periods, rowDefs, colors }) {
  if (!periods?.length) return (
    <p className="text-[12px] py-6 text-center italic" style={{ color: colors.secondaryText, opacity: 0.4 }}>
      Quarterly data not available from yfinance for this statement
    </p>
  );

  // newest on the right (ascending order)
  const cols = [...periods];

  return (
    <div className="overflow-x-auto -mx-3">
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: cols.length * 80 + 130 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
            <th className="text-left py-2 px-3" style={{ minWidth: 130 }}>
              <span className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText, opacity: 0.5 }}>
                ₹ Crores
              </span>
            </th>
            {cols.map((c, ci) => {
              const isLatest = ci === cols.length - 1;
              return (
                <th
                  key={c.period}
                  className="text-right py-2 px-3"
                  style={{
                    minWidth: 80,
                    backgroundColor: isLatest ? `${colors.accent}14` : 'transparent',
                  }}
                >
                  <span
                    className="text-[12px] font-semibold"
                    style={{ color: isLatest ? colors.accent : colors.secondaryText }}
                  >
                    {c.period.replace(/(\w+)\s+(\d{4})/, (_, m, y) => `${m}\n${y}`).split('\n').map((part, i) => (
                      <span key={i} className="block leading-tight">{part}</span>
                    ))}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rowDefs.map((row, ri) => {
            const isLatestIdx = cols.length - 1;
            return (
              <tr
                key={ri}
                style={{
                  borderBottom: `1px solid ${colors.border}`,
                  backgroundColor: row.bold ? `${colors.inputBg}80` : 'transparent',
                }}
              >
                <td className="py-2.5 px-3">
                  <span
                    className="text-[13px]"
                    style={{
                      color: row.isPct ? colors.secondaryText : colors.primaryText,
                      fontWeight: row.bold ? 700 : 400,
                      fontStyle: row.isPct ? 'normal' : 'normal',
                    }}
                  >
                    {row.label}
                  </span>
                </td>
                {cols.map((c, ci) => {
                  const isLatest = ci === isLatestIdx;
                  const display = getRowValue(row, c);
                  const num = getRowNumeric(row, c);
                  const isNeg = num != null && num < 0;
                  let color = colors.primaryText;
                  if (row.isPct) color = colors.secondaryText;
                  else if (row.bold && num != null) color = isNeg ? '#ef4444' : '#16C784';
                  else if (num != null && isNeg) color = '#ef4444';
                  return (
                    <td
                      key={ci}
                      className="py-2.5 px-3 text-right tabular-nums"
                      style={{
                        backgroundColor: isLatest ? `${colors.accent}14` : 'transparent',
                      }}
                    >
                      <span
                        className="text-[13px]"
                        style={{
                          color: display === '—' ? colors.secondaryText : color,
                          fontWeight: row.bold ? 700 : 400,
                          opacity: display === '—' ? 0.3 : 1,
                        }}
                      >
                        {display}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FinancialStatements({ symbol, colors }) {
  const [data, setData] = useState(null);
  const [stmtTab, setStmtTab] = useState('income');
  const [periodTab, setPeriodTab] = useState('quarterly');

  useEffect(() => {
    axios.get(`${API_URL}/stocks/${symbol}/financials`)
      .then(r => setData(r.data))
      .catch(() => {});
  }, [symbol]);

  if (!data) return null;

  const STMT_TABS = [
    { key: 'income',        label: 'Quarterly Results', annualLabel: 'Profit & Loss', rows: PL_ROWS  },
    { key: 'balance_sheet', label: 'Balance Sheet',     annualLabel: 'Balance Sheet', rows: BS_ROWS  },
    { key: 'cashflow',      label: 'Cash Flows',        annualLabel: 'Cash Flows',    rows: CF_ROWS  },
  ];

  const activeStmt = STMT_TABS.find(s => s.key === stmtTab);
  const hasQuarterly = (data[stmtTab]?.quarterly?.length || 0) > 0;
  const effectivePeriod = (!hasQuarterly && periodTab === 'quarterly') ? 'annual' : periodTab;
  const periods = data[stmtTab]?.[effectivePeriod] || [];
  const title = effectivePeriod === 'quarterly' ? activeStmt.label : activeStmt.annualLabel;

  return (
    <div className="app-card rounded-xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
      {/* Header */}
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h2 className="text-[15px] font-bold" style={{ color: colors.primaryText }}>{title}</h2>
            <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText, opacity: 0.6 }}>
              Consolidated Figures in Rs. Crores
            </p>
          </div>
          {/* Annual / Quarterly toggle */}
          <div className="flex rounded-lg overflow-hidden flex-shrink-0" style={{ border: `1px solid ${colors.border}` }}>
            {['quarterly', 'annual'].map(p => {
              const disabled = p === 'quarterly' && !hasQuarterly;
              return (
                <button
                  key={p}
                  onClick={() => !disabled && setPeriodTab(p)}
                  className="px-3 py-1.5 text-[11px] font-medium"
                  style={{
                    backgroundColor: effectivePeriod === p ? colors.accent : 'transparent',
                    color: disabled ? `${colors.secondaryText}40` : effectivePeriod === p ? '#fff' : colors.secondaryText,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  {p === 'annual' ? 'Annual' : 'Quarterly'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Statement type pills */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
          {STMT_TABS.map(s => (
            <button
              key={s.key}
              onClick={() => setStmtTab(s.key)}
              className="px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap flex-shrink-0"
              style={{
                backgroundColor: stmtTab === s.key ? colors.accent : colors.inputBg,
                color: stmtTab === s.key ? '#fff' : colors.secondaryText,
                border: `1px solid ${stmtTab === s.key ? colors.accent : colors.border}`,
              }}
            >
              {s.key === 'income'
                ? (effectivePeriod === 'quarterly' ? 'Quarterly Results' : 'Profit & Loss')
                : s.key === 'balance_sheet' ? 'Balance Sheet' : 'Cash Flows'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <ScreenerTable periods={periods} rowDefs={activeStmt.rows} colors={colors} />
    </div>
  );
}

// ─── Fundamentals — Validation Layer ─────────────────────────────────────────

function FundaMetricRow({ label, sublabel, value, fmt, interp, barMin, barMax, colors }) {
  const i = value != null ? interp(value) : null;
  const barPct = value != null && barMin != null && barMax != null
    ? Math.min(100, Math.max(0, ((value - barMin) / (barMax - barMin)) * 100))
    : null;
  return (
    <div className="flex items-center gap-3 py-2" style={{ borderBottom: `1px solid ${colors.border}60` }}>
      {/* Label */}
      <div className="flex flex-col min-w-0" style={{ flex: '0 0 38%' }}>
        <span className="text-[11px] font-medium leading-tight" style={{ color: colors.primaryText }}>{label}</span>
        {sublabel && <span className="text-[9px] leading-tight mt-0.5" style={{ color: colors.secondaryText, opacity: 0.55 }}>{sublabel}</span>}
      </div>
      {/* Bar + value */}
      <div className="flex flex-col gap-1" style={{ flex: '1 1 0' }}>
        {barPct != null && (
          <div className="rounded-full overflow-hidden" style={{ height: 4, backgroundColor: `${colors.secondaryText}18` }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${barPct}%`, backgroundColor: i ? i.color : colors.secondaryText }} />
          </div>
        )}
      </div>
      {/* Value + badge */}
      <div className="flex flex-col items-end" style={{ flex: '0 0 auto', minWidth: 72 }}>
        <span className="text-[14px] font-bold tabular-nums leading-none" style={{ color: value != null ? (i ? i.color : colors.primaryText) : colors.secondaryText, opacity: value != null ? 1 : 0.35 }}>
          {value != null ? fmt(value) : 'N/A'}
        </span>
        {i && (
          <span className="text-[9px] font-medium mt-0.5 leading-none" style={{ color: i.color }}>{i.label}</span>
        )}
      </div>
    </div>
  );
}

function FundaHealthPill({ label, score, colors }) {
  const cfg = score >= 2 ? { color: '#16C784', bg: '#16C78415', text: 'Strong' }
    : score === 1       ? { color: '#6366F1', bg: '#6366F115', text: 'Fair' }
    : score === 0       ? { color: '#f59e0b', bg: '#f59e0b15', text: 'Watch' }
    :                     { color: '#ef4444', bg: '#ef444415', text: 'Weak' };
  return (
    <div className="flex-1 rounded-xl px-2 py-2 flex flex-col items-center gap-1 text-center" style={{ backgroundColor: cfg.bg, border: `1px solid ${cfg.color}22` }}>
      <span className="text-[10px] font-semibold" style={{ color: colors.secondaryText, opacity: 0.6 }}>{label}</span>
      <span className="text-[11px] font-bold" style={{ color: cfg.color }}>{cfg.text}</span>
    </div>
  );
}

function fmtUpdatedAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return null; }
}

function FundamentalsInsight({ stock, fundamentals, colors }) {
  const SECTION_TIP = 'Shows company health. Profitability and debt levels help determine if the business is strong enough to support trades or long-term positions.';

  // Prefer rich fundamentals data, fall back to stocks table
  const pe     = fundamentals?.trailing_pe  ?? stock.pe_ratio;
  const pb     = fundamentals?.pb_ratio     ?? stock.pb_ratio;
  const divYld = fundamentals?.dividend_yield != null ? fundamentals.dividend_yield * 100 : (stock.dividend_yield ?? null);
  const roe    = fundamentals?.roe != null ? fundamentals.roe * 100 : stock.roe;
  const eps    = fundamentals?.eps_trailing  ?? stock.eps;
  const de     = fundamentals?.debt_to_equity ?? stock.debt_to_equity;

  const groups = [
    {
      label: 'Valuation',
      fields: [
        {
          label: 'P/E (TTM)', value: pe,
          fmt: (v) => `${v.toFixed(1)}×`,
          interp: (v) => v < 15 ? { label: 'Undervalued', color: '#16C784' } : v <= 25 ? { label: 'Fair', color: '#6366F1' } : v <= 40 ? { label: 'Stretched', color: '#f59e0b' } : { label: 'Expensive', color: '#ef4444' },
        },
        {
          label: 'Fwd P/E', value: fundamentals?.forward_pe ?? null,
          fmt: (v) => `${v.toFixed(1)}×`,
          interp: (v) => v < 15 ? { label: 'Cheap', color: '#16C784' } : v <= 25 ? { label: 'Fair', color: '#6366F1' } : v <= 40 ? { label: 'Stretched', color: '#f59e0b' } : { label: 'Expensive', color: '#ef4444' },
        },
        {
          label: 'P/B Ratio', value: pb,
          fmt: (v) => `${v.toFixed(2)}×`,
          interp: (v) => v < 1 ? { label: 'Below book', color: '#16C784' } : v <= 3 ? { label: 'Reasonable', color: '#6366F1' } : v <= 6 ? { label: 'Premium', color: '#f59e0b' } : { label: 'Very high', color: '#ef4444' },
        },
        {
          label: 'PEG Ratio', value: fundamentals?.peg_ratio ?? null,
          fmt: (v) => v.toFixed(2),
          interp: (v) => v < 1 ? { label: 'Undervalued', color: '#16C784' } : v <= 2 ? { label: 'Fair', color: '#6366F1' } : { label: 'Overvalued', color: '#ef4444' },
        },
      ],
    },
    {
      label: 'Profitability',
      fields: [
        {
          label: 'ROE', value: roe,
          fmt: (v) => `${v.toFixed(1)}%`,
          interp: (v) => v >= 25 ? { label: 'Strong', color: '#16C784' } : v >= 15 ? { label: 'Good', color: '#6366F1' } : { label: 'Weak', color: '#ef4444' },
        },
        {
          label: 'Net Margin', value: fundamentals?.profit_margin != null ? fundamentals.profit_margin * 100 : null,
          fmt: (v) => `${v.toFixed(1)}%`,
          interp: (v) => v >= 20 ? { label: 'Excellent', color: '#16C784' } : v >= 10 ? { label: 'Good', color: '#6366F1' } : v >= 0 ? { label: 'Low', color: '#f59e0b' } : { label: 'Loss', color: '#ef4444' },
        },
        {
          label: 'Op. Margin', value: fundamentals?.operating_margin != null ? fundamentals.operating_margin * 100 : null,
          fmt: (v) => `${v.toFixed(1)}%`,
          interp: (v) => v >= 20 ? { label: 'Strong', color: '#16C784' } : v >= 10 ? { label: 'Adequate', color: '#6366F1' } : v >= 0 ? { label: 'Thin', color: '#f59e0b' } : { label: 'Negative', color: '#ef4444' },
        },
        {
          label: 'EPS (TTM)', value: eps,
          fmt: (v) => `₹${v.toFixed(1)}`,
          interp: (v) => v > 0 ? { label: 'Profitable', color: '#16C784' } : { label: 'Loss-making', color: '#ef4444' },
        },
      ],
    },
    {
      label: 'Growth',
      fields: [
        {
          label: 'Rev. Growth', value: fundamentals?.revenue_growth != null ? fundamentals.revenue_growth * 100 : null,
          fmt: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`,
          interp: (v) => v >= 20 ? { label: 'High growth', color: '#16C784' } : v >= 10 ? { label: 'Moderate', color: '#6366F1' } : v >= 0 ? { label: 'Slow', color: '#f59e0b' } : { label: 'Declining', color: '#ef4444' },
        },
        {
          label: 'Earn. Growth', value: fundamentals?.earnings_growth != null ? fundamentals.earnings_growth * 100 : null,
          fmt: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`,
          interp: (v) => v >= 20 ? { label: 'High growth', color: '#16C784' } : v >= 10 ? { label: 'Moderate', color: '#6366F1' } : v >= 0 ? { label: 'Slow', color: '#f59e0b' } : { label: 'Declining', color: '#ef4444' },
        },
        {
          label: 'Div. Yield', value: divYld,
          fmt: (v) => `${v.toFixed(2)}%`,
          interp: (v) => v >= 3 ? { label: 'High yield', color: '#16C784' } : v >= 1 ? { label: 'Moderate', color: '#6366F1' } : { label: 'Low yield', color: '#f59e0b' },
        },
        {
          label: 'Debt / Eq.', value: de,
          fmt: (v) => v.toFixed(2),
          interp: (v) => v <= 0.5 ? { label: 'Low debt', color: '#16C784' } : v <= 1.0 ? { label: 'Moderate', color: '#f59e0b' } : { label: 'High debt', color: '#ef4444' },
        },
      ],
    },
  ];

  const getSummary = () => {
    const roeGood = roe != null && roe >= 20;
    const deHigh  = de != null && de > 1.0;
    const deLow   = de != null && de < 0.5;
    const peHigh  = pe != null && pe > 35;
    const marginGood = fundamentals?.profit_margin != null && fundamentals.profit_margin > 0.15;
    if (roeGood && deLow && marginGood) return 'Strong profitability, healthy margins, low debt — quality business';
    if (roeGood && deLow)   return 'Strong profitability with low debt — quality business';
    if (roeGood && deHigh)  return 'Good returns but high debt — monitor leverage carefully';
    if (peHigh && !roeGood) return 'High valuation with weak profitability — caution on entry';
    if (deHigh)             return 'Elevated debt — moderate risk, watch cash flow';
    if (roeGood)            return 'Solid profitability — fundamentally sound';
    return 'Fundamental data partial — run the fundamentals job for a complete picture';
  };

  // Health scores for the 3 pill indicators
  const valuationScore = pe == null ? -1 : pe < 15 ? 2 : pe <= 25 ? 1 : pe <= 40 ? 0 : -1;
  const profitScore    = roe == null ? -1 : roe >= 25 ? 2 : roe >= 15 ? 1 : roe >= 5 ? 0 : -1;
  const debtScore      = de == null  ? -1 : de <= 0.5 ? 2 : de <= 1.0 ? 1 : de <= 2.0 ? 0 : -1;

  return (
    <div className="app-card rounded-xl px-3 py-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Building className="w-4 h-4" style={{ color: colors.accent }} />
          <h2 className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Fundamentals</h2>
          <SectionTooltip tip={SECTION_TIP} />
        </div>
        <div className="flex items-center gap-2">
          {fundamentals?.updated_at && (
            <span className="text-[10px]" style={{ color: colors.secondaryText, opacity: 0.45 }}>
              {fmtUpdatedAt(fundamentals.updated_at)}
            </span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>Validation</span>
        </div>
      </div>

      {/* 3 health pills */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <FundaHealthPill label="Valuation" score={valuationScore} colors={colors} />
        <FundaHealthPill label="Profitability" score={profitScore} colors={colors} />
        <FundaHealthPill label="Debt" score={debtScore} colors={colors} />
      </div>

      {/* Summary */}
      <p className="text-[10px] mb-3 leading-relaxed px-1" style={{ color: colors.secondaryText, opacity: 0.75 }}>
        {getSummary()}
      </p>

      {/* Metric rows */}
      <div className="flex flex-col">
        {/* Valuation */}
        <p className="text-[9px] uppercase tracking-widest font-semibold mb-1 mt-0.5" style={{ color: colors.secondaryText, opacity: 0.4 }}>Valuation</p>
        {groups[0].fields.map((f, i) => (
          <FundaMetricRow key={i} {...f}
            sublabel={f.label === 'P/E (TTM)' ? 'Price vs earnings (lower = cheaper)' : f.label === 'Fwd P/E' ? 'Expected future earnings price' : f.label === 'P/B Ratio' ? 'Price vs book value of assets' : 'Growth-adjusted valuation'}
            barMin={f.label === 'PEG Ratio' ? 0 : 0} barMax={f.label === 'PEG Ratio' ? 3 : f.label.includes('P/B') ? 8 : 50}
            colors={colors} />
        ))}

        {/* Profitability */}
        <p className="text-[9px] uppercase tracking-widest font-semibold mb-1 mt-3" style={{ color: colors.secondaryText, opacity: 0.4 }}>Profitability</p>
        {groups[1].fields.map((f, i) => (
          <FundaMetricRow key={i} {...f}
            sublabel={f.label === 'ROE' ? 'Return on shareholders money' : f.label === 'Net Margin' ? 'Profit kept per ₹100 of revenue' : f.label === 'Op. Margin' ? 'Profit from core business' : 'Earnings per share'}
            barMin={0} barMax={f.label === 'ROE' ? 50 : f.label === 'EPS (TTM)' ? null : 40}
            colors={colors} />
        ))}

        {/* Growth & Risk */}
        <p className="text-[9px] uppercase tracking-widest font-semibold mb-1 mt-3" style={{ color: colors.secondaryText, opacity: 0.4 }}>Growth & Risk</p>
        {groups[2].fields.map((f, i) => (
          <FundaMetricRow key={i} {...f}
            sublabel={f.label === 'Rev. Growth' ? 'Revenue growth year-over-year' : f.label === 'Earn. Growth' ? 'Profit growth year-over-year' : f.label === 'Div. Yield' ? 'Annual dividend vs share price' : 'Debt relative to equity (lower = safer)'}
            barMin={f.label === 'Debt / Eq.' ? 0 : -20} barMax={f.label === 'Debt / Eq.' ? 4 : f.label === 'Div. Yield' ? 8 : 40}
            colors={colors} />
        ))}
      </div>

      {!fundamentals && (
        <p className="text-[10px] italic text-center pt-2" style={{ color: colors.secondaryText, opacity: 0.45 }}>
          Extended data loads after the weekly fundamentals job runs
        </p>
      )}
    </div>
  );
}

// ─── News Sentiment helpers ───────────────────────────────────────────────────

function Peers({ symbol, sector, colors }) {
  const [peers, setPeers] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/stocks/${symbol}/peers`)
      .then(r => setPeers(r.data))
      .catch(() => {});
  }, [symbol]);

  if (!peers?.length) return null;

  return (
    <div className="app-card rounded-xl px-3 py-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4" style={{ color: colors.accent }} />
        <h2 className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Peers</h2>
        {sector && <span className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>{sector}</span>}
      </div>
      <div className="overflow-x-auto -mx-3 px-3">
        <table className="w-full min-w-[380px]" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Stock','CMP','P/E','Mkt Cap','ROE'].map((h, i) => (
                <th key={h} className={`pb-2 text-[9px] uppercase tracking-widest ${i > 0 ? 'text-right' : 'text-left'}`} style={{ color: colors.secondaryText, opacity: 0.5 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {peers.map((p, i) => {
              const isCurrent = p.symbol === symbol;
              return (
                <tr
                  key={p.symbol}
                  style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: isCurrent ? `${colors.accent}0a` : 'transparent' }}
                  onClick={() => !isCurrent && window.location.assign(`/stock/${p.symbol}`)}
                  className={!isCurrent ? 'cursor-pointer' : ''}
                >
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1.5">
                      {isCurrent && <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors.accent }} />}
                      <span className="text-[12px] font-semibold" style={{ color: isCurrent ? colors.accent : colors.primaryText }}>
                        {p.symbol.replace('.NS','')}
                      </span>
                    </div>
                    <p className="text-[9px] truncate max-w-[90px]" style={{ color: colors.secondaryText }}>{p.company_name}</p>
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums">
                    <span className="text-[11px]" style={{ color: colors.primaryText }}>{p.current_price != null ? `₹${Number(p.current_price).toFixed(0)}` : '—'}</span>
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums">
                    <span className="text-[11px]" style={{ color: colors.secondaryText }}>{p.pe_ratio != null ? `${Number(p.pe_ratio).toFixed(1)}×` : '—'}</span>
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums">
                    <span className="text-[11px]" style={{ color: colors.secondaryText }}>{p.market_cap != null ? fmtCr(p.market_cap / 10000000) : '—'}</span>
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums">
                    <span className="text-[11px] font-medium" style={{ color: p.roe != null ? (p.roe >= 0.15 ? '#16C784' : p.roe >= 0.10 ? '#f59e0b' : '#ef4444') : colors.secondaryText }}>
                      {p.roe != null ? `${(p.roe * 100).toFixed(1)}%` : '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnalystTargets({ fundamentals, currentPrice, colors }) {
  if (!fundamentals?.target_mean && !fundamentals?.recommendation) return null;
  const { target_mean, target_high, target_low, recommendation, analyst_count } = fundamentals;

  const recColors = {
    strong_buy:    { bg: '#16C78420', text: '#16C784', label: 'Strong Buy' },
    buy:           { bg: '#22c55e20', text: '#22c55e', label: 'Buy' },
    hold:          { bg: '#f59e0b20', text: '#f59e0b', label: 'Hold' },
    underperform:  { bg: '#ef444420', text: '#ef4444', label: 'Underperform' },
    sell:          { bg: '#ef444420', text: '#ef4444', label: 'Sell' },
  };
  const rec = recColors[recommendation] || { bg: colors.inputBg, text: colors.secondaryText, label: recommendation || 'N/A' };

  const upside = target_mean && currentPrice ? ((target_mean - currentPrice) / currentPrice * 100) : null;

  return (
    <div className="app-card rounded-xl px-3 py-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" style={{ color: colors.accent }} />
          <h2 className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Analyst Consensus</h2>
        </div>
        {analyst_count != null && (
          <span className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
            {analyst_count} analysts
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <span className="px-3 py-1.5 rounded-lg text-[13px] font-bold" style={{ backgroundColor: rec.bg, color: rec.text }}>
          {rec.label}
        </span>
        {upside != null && (
          <span className="text-[12px] font-semibold" style={{ color: upside >= 0 ? '#16C784' : '#ef4444' }}>
            {upside >= 0 ? '▲' : '▼'} {Math.abs(upside).toFixed(1)}% upside
          </span>
        )}
      </div>

      {target_mean != null && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[
            { label: 'Low',    value: target_low,  color: '#ef4444' },
            { label: 'Mean',   value: target_mean, color: colors.primaryText },
            { label: 'High',   value: target_high, color: '#16C784' },
          ].map(t => (
            <div key={t.label} className="rounded-xl px-2.5 py-2 text-center" style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}>
              <p className="text-[9px] uppercase tracking-wide mb-1" style={{ color: colors.secondaryText }}>{t.label}</p>
              <p className="text-[14px] font-bold" style={{ color: t.value != null ? t.color : colors.secondaryText }}>
                {t.value != null ? `₹${Number(t.value).toFixed(0)}` : '—'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BusinessSummary({ fundamentals, colors, inline = false }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!fundamentals?.business_summary) return null;
  const text = fundamentals.business_summary;
  const previewLen = inline ? 160 : 200;
  const preview = text.length > previewLen ? text.slice(0, previewLen) + '…' : text;

  const inner = (
    <>
      {!inline && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4" style={{ color: colors.accent }} />
            <h2 className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>About</h2>
          </div>
          {fundamentals.website && (
            <a href={fundamentals.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[11px]" style={{ color: colors.accent }}>
              Website <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
      <p className="text-[11px] leading-relaxed" style={{ color: colors.secondaryText, opacity: inline ? 0.75 : 1 }}>
        {expanded ? text : preview}
      </p>
      {text.length > previewLen && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-1 text-[10px] flex items-center gap-1"
          style={{ color: colors.accent }}
        >
          {expanded ? (<><ChevronUp className="w-3 h-3" /> Show less</>) : (<><ChevronDown className="w-3 h-3" /> Read more</>)}
        </button>
      )}
    </>
  );

  if (inline) return <div>{inner}</div>;

  return (
    <div className="app-card rounded-xl px-3 py-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
      {inner}
    </div>
  );
}

function formatEventDetail(event) {
  const d = event.details || {};
  switch (event.event_type) {
    case 'DIVIDEND':
      return d.amount != null ? `₹${Number(d.amount).toFixed(2)} per share` : 'Dividend announced';
    case 'SPLIT':
      return d.ratio ? `Split ratio: ${d.ratio}` : 'Stock split';
    case 'EARNINGS':
      if (d.reported_eps != null) {
        const surp = d.surprise_pct != null ? ` (${d.surprise_pct > 0 ? '+' : ''}${Number(d.surprise_pct).toFixed(1)}% surprise)` : '';
        return `EPS: ₹${Number(d.reported_eps).toFixed(2)}${surp}`;
      }
      if (d.eps_estimate != null) return `Est. EPS: ₹${Number(d.eps_estimate).toFixed(2)}`;
      return 'Quarterly results';
    default:
      return '';
  }
}

function eventExplainer(event) {
  const d = event.details || {};
  switch (event.event_type) {
    case 'DIVIDEND':
      return d.amount != null
        ? `The company is paying ₹${Number(d.amount).toFixed(2)} to each share you hold. Bullish signal — company is profitable and returning cash to investors.`
        : 'Company is sharing profits with shareholders. Positive for long-term holders.';
    case 'SPLIT':
      return `The share price will drop proportionally and you'll receive more shares. Your total investment value stays the same — only the price per share changes.`;
    case 'EARNINGS': {
      const surp = d.surprise_pct;
      if (surp != null) {
        if (surp > 5)  return `Earnings beat estimates by ${surp.toFixed(1)}% — strong result. Often triggers a bullish price move.`;
        if (surp < -5) return `Earnings missed estimates by ${Math.abs(surp).toFixed(1)}% — weak result. Often triggers a sell-off.`;
        return `Earnings were in line with estimates. Neutral impact expected.`;
      }
      return 'Quarterly profit/loss report. Compare EPS to last quarter and analyst estimates to judge direction.';
    }
    default:
      return null;
  }
}

function eventTypeColor(type) {
  if (type === 'DIVIDEND') return '#16C784';
  if (type === 'SPLIT')    return '#6366f1';
  if (type === 'EARNINGS') return '#f59e0b';
  return '#94a3b8';
}

function labelColorFor(label) {
  if (!label) return '#94a3b8';
  if (label.includes('Positive')) return '#16C784';
  if (label.includes('Negative')) return '#ef4444';
  return '#94a3b8';
}

function timeAgo(iso) {
  if (!iso) return '—';
  const h = Math.floor((Date.now() - new Date(iso)) / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function isNSEMarketOpen() {
  const now = new Date();
  const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  const day = ist.getUTCDay(); // 0=Sun, 6=Sat
  const mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
  return day >= 1 && day <= 5 && mins >= 9 * 60 + 15 && mins <= 15 * 60 + 30;
}

const INDEX_DISPLAY_NAMES = { '^NSEI': 'NIFTY 50', '^NSEBANK': 'BANK NIFTY' };

const ALL_SECTIONS = [
  { id: 'sec-overview',     label: 'Overview'     },
  { id: 'sec-chart',        label: 'Chart'        },
  { id: 'sec-technicals',   label: 'Technicals'   },
  { id: 'sec-fundamentals', label: 'Fundamentals', stockOnly: true },
  { id: 'sec-financials',   label: 'Financials',   stockOnly: true },
  { id: 'sec-peers',        label: 'Peers',        stockOnly: true },
  { id: 'sec-shareholding', label: 'Shareholding', stockOnly: true },
  { id: 'sec-events',       label: 'Events',       stockOnly: true },
];

const INDEX_SECTIONS = [
  { id: 'sec-chart', label: 'Chart' },
  { id: 'sec-ohlcv', label: 'OHLCV' },
];

function SectionNavBar({ activeId, colors, sections }) {
  const navRef = React.useRef(null);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Scroll the nav bar horizontally to keep active pill centred — never touches page scroll
  React.useEffect(() => {
    if (!navRef.current || !activeId) return;
    const btn = navRef.current.querySelector(`[data-secid="${activeId}"]`);
    if (!btn) return;
    const nav = navRef.current;
    const target = btn.offsetLeft - nav.offsetWidth / 2 + btn.offsetWidth / 2;
    nav.scrollTo({ left: target, behavior: 'smooth' });
  }, [activeId]);

  return (
    <div
      className="sticky top-0 z-30 -mx-4 px-4 py-0"
      style={{ backgroundColor: colors.background || colors.cardBg, borderBottom: `1px solid ${colors.border}` }}
    >
      <div
        ref={navRef}
        className="flex gap-0 overflow-x-auto"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {sections.map(s => {
          const isActive = activeId === s.id;
          return (
            <button
              key={s.id}
              data-secid={s.id}
              onClick={() => scrollTo(s.id)}
              className="flex-shrink-0 px-4 py-3 text-[13px] font-medium relative transition-colors"
              style={{ color: isActive ? colors.accent : colors.secondaryText, whiteSpace: 'nowrap' }}
            >
              {s.label}
              {isActive && (
                <span
                  className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full"
                  style={{ backgroundColor: colors.accent }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StockDetailPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { colors, theme } = useTheme();
  const { user } = useAuth();
  const { prices } = useSocket();
  const isIndex = symbol?.startsWith('^');
  const sections = isIndex ? INDEX_SECTIONS : ALL_SECTIONS;
  const [stock, setStock] = useState(null);
  const [technicals, setTechnicals] = useState(null);
  const [events, setEvents] = useState([]);
  const [fundamentals, setFundamentals] = useState(null);
  const [scannerSignals, setScannerSignals] = useState([]);
  const [chartPatterns, setChartPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [indicatorTab, setIndicatorTab] = useState('intraday');
  const [priceFlash, setPriceFlash] = useState(null); // 'up' | 'down' | null
  const prevPriceRef = useRef(null);
  const [activeSection, setActiveSection] = useState(symbol?.startsWith('^') ? 'sec-chart' : 'sec-overview');
  const simActive = true;
  const { watchlists, fetchWatchlists, addToWatchlist, isInWatchlist } = useWatchlist();
  const [watchlistPopup, setWatchlistPopup] = useState(false);
  const [chartLearnOpen, setChartLearnOpen] = useState(false);

  // Order Modal State
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderType, setOrderType] = useState('BUY');


  // IntersectionObserver — track which section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost section that is intersecting
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveSection(visible[0].target.id);
      },
      { threshold: 0.15, rootMargin: '-60px 0px -40% 0px' }
    );
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading]); // re-run after data loads

  useEffect(() => {
    fetchWatchlists();
    fetchStockData();
  }, [symbol]);

  const socketSymbol = stock?.symbol || (symbol.endsWith('.NS') ? symbol : `${symbol}.NS`);
  const socketPrice = prices[socketSymbol] ?? prices[symbol] ?? null;

  useEffect(() => {
    if (!socketPrice) return;
    if (prevPriceRef.current !== null && socketPrice.price !== prevPriceRef.current) {
      const dir = socketPrice.price > prevPriceRef.current ? 'up' : 'down';
      setPriceFlash(dir);
      setTimeout(() => setPriceFlash(null), 250);
    }
    prevPriceRef.current = socketPrice.price;
  }, [socketPrice?.price]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddToWatchlist = () => {
    fetchWatchlists();
    setWatchlistPopup(true);
  };

  const handleSelectWatchlist = async (wlId) => {
    await addToWatchlist(wlId, symbol);
    setWatchlistPopup(false);
  };

  const fetchStockData = async () => {
    try {
      setLoading(true);
      const baseRequests = [
        axios.get(`${API_URL}/stocks/${symbol}`),
        axios.get(`${API_URL}/stocks/${symbol}/technicals`).catch(() => ({ data: null })),
      ];
      if (!isIndex) {
        baseRequests.push(
          axios.get(`${API_URL}/stocks/${symbol}/events`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/scanners/results?limit=50`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/patterns`).catch(() => ({ data: { patterns: [] } })),
          axios.get(`${API_URL}/stocks/${symbol}/fundamentals`).catch(() => ({ data: null })),
        );
      }
      const results = await Promise.all(baseRequests);
      const [stockRes, techRes] = results;
      setStock(stockRes.data);
      setTechnicals(techRes.data);
      if (!isIndex) {
        const [,,eventsRes, scannersRes, fundRes, patternsRes] = results;
        setEvents(eventsRes.data || []);
        if (fundRes.data?.available) setFundamentals(fundRes.data);
        const sym = symbol.endsWith('.NS') ? symbol : `${symbol}.NS`;
        const stockSignals = (scannersRes.data || []).filter(s =>
          s.symbol === symbol || s.symbol === sym
        );
        setScannerSignals(stockSignals);
        const stockPatterns = (patternsRes.data?.patterns || []).filter(p =>
          p.symbol === symbol || p.symbol === sym
        );
        setChartPatterns(stockPatterns);
      } else {
        // Clear stock-only state so stale data from a previous equity page doesn't bleed through
        setEvents([]);
        setFundamentals(null);
        setScannerSignals([]);
        setChartPatterns([]);
      }
    } catch (error) {
      console.error('Error fetching stock data:', error);
      toast.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  };




  const handleTrade = (type) => {
    setOrderType(type);
    setOrderModalOpen(true);
  };

  const formatLargeNumber = (num) => {
    if (num == null || isNaN(num) || num === 0) return '—';
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
    return formatNumber(num, 0);
  };

  const fmtPct = (value, decimals = 2) => {
    if (value == null || isNaN(value)) return '—';
    return `${Number(value).toFixed(decimals)}%`;
  };

  if (loading) return <DetailPageSkeleton />;

  if (!stock) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: colors.loss }} />
        <p style={{ color: colors.secondaryText }}>Stock not found</p>
      </div>
    );
  }

  const livePrice = socketPrice;
  const displayPrice = livePrice?.price ?? stock.current_price;
  const displayChangePct = livePrice != null ? livePrice.change_pct : stock.change_pct;
  const isPositive = displayChangePct >= 0;
  const isLive = !!(livePrice || stock.live);
  const isStale = false;

  const flashBg =
    priceFlash === 'up'   ? 'rgba(22,163,74,0.12)' :
    priceFlash === 'down' ? 'rgba(220,38,38,0.12)'  : 'transparent';

  return (
    <div className="space-y-6 pb-24 overflow-x-hidden" data-testid="stock-detail-page">
      {/* Back Button + Header */}
      <div className="flex items-center space-x-4" id="sec-header">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg transition-colors"
          style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }}
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" style={{ color: colors.primaryText }} />
        </button>
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <h1 className="text-[20px] font-bold" style={{ color: colors.primaryText }}>
              {isIndex ? (INDEX_DISPLAY_NAMES[symbol] || symbol) : stock.symbol.replace('.NS', '')}
            </h1>
            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
              NSE
            </span>
            {simActive && (
              <span className="text-xs px-2 py-1 rounded font-semibold" style={{ backgroundColor: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}>
                SIM
              </span>
            )}
          </div>
          <p className="text-[13px]" style={{ color: colors.secondaryText }}>{stock.company_name}</p>
        </div>
        <div className="relative">
          <button
            onClick={handleAddToWatchlist}
            className="p-2 rounded-lg transition-colors"
            style={{ backgroundColor: isInWatchlist(symbol) ? 'rgba(99,102,241,0.15)' : colors.cardBg }}
            data-testid="add-watchlist-button"
            title={isInWatchlist(symbol) ? 'In watchlist' : 'Add to watchlist'}
          >
            <Star className="w-5 h-5" style={{ color: colors.accent, fill: isInWatchlist(symbol) ? colors.accent : 'none' }} />
          </button>

          {watchlistPopup && (
            <>
              {/* Backdrop */}
              <div className="fixed inset-0 z-40" onClick={() => setWatchlistPopup(false)} />
              {/* Dropdown */}
              <div
                className="app-card absolute right-0 top-10 z-50 rounded-xl shadow-xl py-1 min-w-[160px]"
                style={{ backgroundColor: colors.cardBg, borderWidth: '1px', borderColor: colors.accentBorder }}
              >
                <p className="px-3 py-1.5 text-[13px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>
                  Add to watchlist
                </p>
                {watchlists.map(wl => {
                  const inThis = (wl.stocks || []).includes(symbol.endsWith('.NS') ? symbol : `${symbol}.NS`);
                  return (
                    <button
                      key={wl.id}
                      onClick={() => handleSelectWatchlist(wl.id)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm transition-colors hover:opacity-80"
                      style={{ color: inThis ? colors.accent : colors.primaryText }}
                    >
                      <span>{wl.name}</span>
                      {inThis && <Star className="w-3 h-3" style={{ fill: colors.accent, color: colors.accent }} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sticky Section Nav */}
      <SectionNavBar activeId={activeSection} colors={colors} sections={sections} />

      {/* Price Header */}
      <div id={isIndex ? undefined : 'sec-overview'}
        className="rounded-xl px-3 py-2"
        style={{
          backgroundColor: flashBg !== 'transparent' ? flashBg : colors.cardBg,
          borderWidth: '1px',
          borderColor: colors.border,
          transition: 'background-color 250ms ease-out',
        }}
      >
        <div className="flex items-center justify-between gap-2">
          {/* Price + change — left side, can shrink */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[22px] font-bold leading-none flex-shrink-0" style={{ color: colors.primaryText }}>
                {formatPrice(displayPrice)}
              </p>
              <div className="flex items-center gap-1">
                {isPositive ? (
                  <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.profit }} />
                ) : (
                  <TrendingDown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.loss }} />
                )}
                <span className="text-[13px] font-semibold" style={{ color: isPositive ? colors.profit : colors.loss }}>
                  {isPositive ? '+' : ''}{displayChangePct?.toFixed(2)}%
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              {isLive && !isStale && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold animate-pulse" style={{ backgroundColor: '#16a34a22', color: '#16a34a' }}>
                  {simActive ? 'LIVE-S' : 'LIVE'}
                </span>
              )}
              {isStale && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: 'rgba(234,179,8,0.15)', color: '#ca8a04' }}>
                  ⚠ Delayed
                </span>
              )}
            </div>
          </div>
          {/* BUY / SELL — right side, never shrink */}
          {!isIndex && (
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => handleTrade('BUY')}
                className="px-4 py-2 rounded-lg font-semibold text-white text-[13px]"
                style={{ backgroundColor: colors.accent }}
                data-testid="buy-button"
              >
                <ShoppingCart className="w-3.5 h-3.5 inline mr-1" />BUY
              </button>
              <button
                onClick={() => handleTrade('SELL')}
                className="px-4 py-2 rounded-lg font-semibold text-white text-[13px]"
                style={{ backgroundColor: '#ef4444' }}
                data-testid="sell-button"
              >
                SELL
              </button>
            </div>
          )}
        </div>

        {/* Inline Quick Stats strip — stocks only; indices get a dedicated OHLCV section */}
        {!isIndex && (
          <>
            <div className="mt-2 pt-2 flex flex-wrap gap-1.5 overflow-x-auto" style={{ borderTop: `1px solid ${colors.border}40` }}>
              {[
                { label: 'Open', value: formatPrice(stock.open) },
                { label: 'High', value: formatPrice(stock.high), valueColor: colors.profit },
                { label: 'Low', value: formatPrice(stock.low), valueColor: colors.loss },
                { label: 'Prev Close', value: formatPrice(stock.prev_close) },
                { label: 'Volume', value: formatLargeNumber(stock.volume ?? technicals?.volume) },
                { label: 'Mkt Cap', value: stock.market_cap != null ? `${formatNumber(stock.market_cap, 0)} Cr` : '—' },
              ].map((stat, idx) => (
                <div key={idx}
                  className="flex items-center gap-1.5 whitespace-nowrap px-2 py-1 rounded-lg"
                  style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}50` }}
                >
                  <span className="text-[9px] uppercase tracking-wide font-medium" style={{ color: colors.secondaryText, opacity: 0.55 }}>{stat.label}</span>
                  <span className="text-[11px] font-bold tabular-nums" style={{ color: stat.valueColor ?? colors.primaryText }}>{stat.value}</span>
                </div>
              ))}
              {/* 52W Range as 7th badge */}
              {stock.low_52week != null && stock.high_52week != null && (
                <div className="flex items-center gap-1.5 whitespace-nowrap px-2 py-1 rounded-lg"
                  style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}50` }}
                >
                  <span className="text-[9px] uppercase tracking-wide font-medium" style={{ color: colors.secondaryText, opacity: 0.55 }}>52W</span>
                  <span className="text-[11px] font-bold tabular-nums" style={{ color: '#ef4444' }}>
                    {stock.low_52week >= 1000 ? `₹${(stock.low_52week/1000).toFixed(0)}K` : `₹${Math.round(stock.low_52week)}`}
                  </span>
                  <div className="relative h-1 rounded-full overflow-visible" style={{ width: 36, background: 'linear-gradient(to right, #ef4444, #16C784)' }}>
                    <div className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white border border-gray-300"
                      style={{ left: `calc(${Math.min(100,Math.max(0,((displayPrice-stock.low_52week)/(stock.high_52week-stock.low_52week))*100))}% - 3px)` }} />
                  </div>
                  <span className="text-[11px] font-bold tabular-nums" style={{ color: '#16C784' }}>
                    {stock.high_52week >= 1000 ? `₹${(stock.high_52week/1000).toFixed(0)}K` : `₹${Math.round(stock.high_52week)}`}
                  </span>
                </div>
              )}
            </div>

            {/* About — inline below price */}
            {fundamentals?.business_summary && (
              <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${colors.border}40` }}>
                <BusinessSummary fundamentals={fundamentals} colors={colors} inline />
              </div>
            )}
          </>
        )}
      </div>

      {/* Price Chart */}
      <div id="sec-chart">
      <TradingChart
        symbol={symbol}
        height={480}
      />
      </div>

      {/* OHLCV Section — indices only */}
      {isIndex && (
        <div id="sec-ohlcv">
          <div className="rounded-xl p-4" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: colors.secondaryText }}>OHLCV</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Open',       value: formatPrice(stock.open),       color: colors.primaryText },
                { label: 'High',       value: formatPrice(stock.high),       color: colors.profit },
                { label: 'Low',        value: formatPrice(stock.low),        color: colors.loss },
                { label: 'Prev Close', value: formatPrice(stock.prev_close), color: colors.primaryText },
                { label: 'Volume',     value: formatLargeNumber(stock.volume ?? technicals?.volume), color: colors.primaryText },
                { label: 'Change',     value: `${displayChangePct >= 0 ? '+' : ''}${displayChangePct?.toFixed(2)}%`, color: displayChangePct >= 0 ? colors.profit : colors.loss },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-lg p-2.5 text-center" style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}50` }}>
                  <p className="text-[13px] font-bold tabular-nums" style={{ color }}>{value}</p>
                  <p className="text-[9px] uppercase tracking-wide mt-0.5" style={{ color: colors.secondaryText }}>{label}</p>
                </div>
              ))}
            </div>
            {stock.low_52week != null && stock.high_52week != null && (
              <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${colors.border}40` }}>
                <p className="text-[9px] uppercase tracking-widest font-bold mb-2" style={{ color: colors.secondaryText }}>52-Week Range</p>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold tabular-nums" style={{ color: colors.loss }}>
                    ₹{stock.low_52week >= 1000 ? `${(stock.low_52week/1000).toFixed(1)}K` : Math.round(stock.low_52week)}
                  </span>
                  <div className="flex-1 relative h-1.5 rounded-full" style={{ background: `linear-gradient(to right, ${colors.loss}, ${colors.profit})` }}>
                    <div className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-white shadow"
                      style={{ left: `calc(${Math.min(100,Math.max(0,((displayPrice-stock.low_52week)/(stock.high_52week-stock.low_52week))*100))}% - 5px)` }} />
                  </div>
                  <span className="text-[11px] font-bold tabular-nums" style={{ color: colors.profit }}>
                    ₹{stock.high_52week >= 1000 ? `${(stock.high_52week/1000).toFixed(1)}K` : Math.round(stock.high_52week)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Technical Indicators — stocks only */}
      {!isIndex && (
        <div id="sec-technicals">
          {/* Learning nudge */}
          <div className="rounded-xl overflow-hidden mb-3" style={{ background: chartLearnOpen ? 'rgba(255,255,255,0.03)' : 'linear-gradient(135deg, #4ecca318 0%, #4ecca308 100%)', border: chartLearnOpen ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(78,204,163,0.35)', transition: 'background 0.2s, border-color 0.2s' }}>
            <button onClick={() => setChartLearnOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5" style={{ color: chartLearnOpen ? 'rgba(255,255,255,0.4)' : '#4ecca3' }} />
                <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: chartLearnOpen ? 'rgba(255,255,255,0.4)' : '#6ee7b7' }}>Decode these signals</p>
              </div>
              <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200" style={{ color: chartLearnOpen ? 'rgba(255,255,255,0.25)' : '#4ecca399', transform: chartLearnOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
            </button>
            {chartLearnOpen && (
              <div className="px-4 pb-4 space-y-1.5">
                {[
                  { level: 6, color: '#6366f1', label: 'Reading Charts Like a Story', sub: 'How to interpret price action and trends' },
                  { level: 8, color: '#4ecca3', label: 'Chart Patterns',              sub: 'Recognise breakouts, reversals and continuations' },
                ].map(({ level, color, label, sub }) => (
                  <button key={level} onClick={() => navigate(`/learning?view=level&level=${level}&card=0`)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
                    style={{ background: `${color}0d`, border: `1px solid ${color}22` }}>
                    <div className="flex items-center gap-2.5">
                      <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                      <div>
                        <p className="text-[11px] font-semibold leading-snug" style={{ color }}>{label}</p>
                        <p className="text-[9px] leading-snug" style={{ color: 'rgba(255,255,255,0.35)' }}>{sub}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: `${color}50` }} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <TechnicalSignals
            technicals={technicals}
            stock={stock}
            colors={colors}
            scannerSignals={scannerSignals}
            chartPatterns={chartPatterns}
          />
          <EdgeScore symbol={symbol} colors={colors} />
        </div>
      )}

      {/* Financial Statements — stocks only */}
      {!isIndex && (
        <div id="sec-financials">
          <FinancialStatements symbol={symbol} colors={colors} />
        </div>
      )}

      {/* Fundamentals — stocks only */}
      {!isIndex && (
        <div id="sec-fundamentals" className="space-y-6">
          <FundamentalsInsight stock={stock} fundamentals={fundamentals} colors={colors} />
          <AnalystTargets fundamentals={fundamentals} currentPrice={displayPrice} colors={colors} />
        </div>
      )}

      {/* Shareholding — stocks only */}
      {!isIndex && (
        <div id="sec-shareholding">
          <ShareholdingInsight stock={stock} colors={colors} />
        </div>
      )}

      {/* Peers — stocks only */}
      {!isIndex && (
        <div id="sec-peers">
          <Peers symbol={symbol} sector={stock.sector} colors={colors} />
        </div>
      )}

      {/* Corporate Events — stocks only */}
      {!isIndex && <div id="sec-events">
      {events.length > 0 && (
        <div className="app-card rounded-xl px-3 py-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder }}>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4" style={{ color: colors.accent }} />
            <h2 className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Corporate Events</h2>
          </div>
          <div className="flex flex-col">
            {events.map((event, idx) => {
              const evColor = eventTypeColor(event.event_type);
              const detail  = formatEventDetail(event);
              const explain = eventExplainer(event);
              const isFuture = new Date(event.date) > new Date();
              const typeLabel = event.event_type === 'DIVIDEND' ? 'Dividend' : event.event_type === 'SPLIT' ? 'Stock Split' : event.event_type === 'EARNINGS' ? 'Earnings' : event.event_type;
              return (
                <div key={idx}
                  className="py-2.5"
                  style={{ borderBottom: idx < events.length - 1 ? `1px solid ${colors.border}40` : 'none' }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: evColor }} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[12px] font-semibold" style={{ color: evColor }}>{typeLabel}</p>
                          {detail && <p className="text-[11px]" style={{ color: colors.secondaryText }}>{detail}</p>}
                        </div>
                        {explain && (
                          <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: colors.secondaryText, opacity: 0.65 }}>{explain}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-[11px] tabular-nums font-medium" style={{ color: isFuture ? evColor : colors.secondaryText }}>
                        {event.date}
                      </span>
                      {isFuture && <p className="text-[9px] mt-0.5 font-semibold" style={{ color: evColor }}>Upcoming</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>}

      {/* Order Modal */}
      <OrderModal
        isOpen={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        stock={stock ? { ...stock, current_price: livePrice?.price ?? stock.current_price } : stock}
        orderType={orderType}
      />
    </div>
  );
}

export default StockDetailPage;
